import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import Pagination from '@/client/components/common/Pagination';
import { toast } from 'sonner';
import {
  SALES_VERTICALS,
  VERTICAL_LABEL,
  type SalesNearMatch,
  type SalesSearchHit,
  type SalesSearchPresence,
  type SalesSearchSort,
  type SalesSearchTier,
  type SalesSellerKind,
  type SalesVertical,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { DuplicateMatchesDialog } from './components/DuplicateMatchesDialog';
import {
  CopyLinkButton,
  EmptyState,
  MarketplaceIndexState,
  PhoneValue,
  Segmented,
  StatusBadge,
  btnGhost,
  btnPrimary,
  compactGhs,
  toastSales,
} from './components/sales-ui';
import {
  usePoolSearchHitMutation,
  useSalesMeQuery,
  useSalesRegionsQuery,
  useSalesSearchQuery,
  useSkipSearchHitMutation,
} from '@/client/api/sales/sales';
import { getApiErrorCode, getApiErrorField } from '@/client/utils/errors';
import { promptActAs, useCurrentRep } from './sales-store';
import useDebouncedSearch from '@/client/hooks/useDebouncedSearch';
import { useListFilters } from '@/client/hooks/useListFilters';

const FILTER_SELECT =
  'h-10 w-full rounded-lg border border-gray-400 bg-white px-3 text-sm text-gray-900 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900';

function FilterField({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-800">
        {label}
      </label>
      {children}
    </div>
  );
}

/** '' -> no filter; anything else as a number. */
const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

/** Inventory bands as `min-max` (either side may be empty). */
const INVENTORY_BANDS = [
  { value: '', label: 'Any' },
  { value: '-100000', label: 'Under GHS 100k' },
  { value: '100000-1000000', label: 'GHS 100k – 1m' },
  { value: '1000000-10000000', label: 'GHS 1m – 10m' },
  { value: '10000000-100000000', label: 'GHS 10m – 100m' },
  { value: '100000000-', label: 'GHS 100m+' },
];
const LIVE_LISTINGS = ['1', '5', '10', '25', '50', '100'];
const POSTED_30D = ['1', '3', '5', '10', '25'];


function seenLabel(iso: string | null | undefined, now: number): string {
  if (!iso) return '—';
  const h = Math.round((now - Date.parse(iso)) / 3_600_000);
  if (!Number.isFinite(h)) return '—';
  if (h < 24) return `${Math.max(0, h)}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function SalesResearchPage() {
  const navigate = useNavigate();
  const rep = useCurrentRep();
  const me = useSalesMeQuery();
  const canWrite = Boolean(rep) && me.data?.canWriteProspects !== false;
  // Debounced: the raw value drives the box, `debounced` drives the query,
  // so typing a name is one request, not one per keystroke.
  // Filters, search and pagination live in the URL (useListFilters) so a search
  // is shareable, bookmarkable and survives reload. Region keeps its own
  // debounced typeahead box, mirrored to the URL. Derived consts + setter shims
  // below keep every downstream read, onChange handler and the reset unchanged.
  const {
    searchTerm: qValue,
    handleSearch: setQ,
    debouncedSearchTerm: q,
    filters,
    handleFilterChange,
    page,
    limit,
    handlePageChange,
    handleLimitChange,
  } = useListFilters({
    initialFilters: {
      vertical: 'all' as SalesVertical | 'all',
      tier: 'paying' as SalesSearchTier | 'all',
      recency: 'all',
      notInPool: '', // '' = off, '1' = on
      hasPhone: '',
      presence: 'all' as SalesSearchPresence | 'all',
      sellerKind: 'all' as SalesSellerKind | 'all',
      inventoryBand: '',
      minListings: '',
      minPosted30: '',
      sort: 'value' as SalesSearchSort,
      region: '',
    },
    initialPage: 1,
    initialLimit: 20,
    debounceMs: 350,
  });
  const regionBox = useDebouncedSearch(filters.region, { delay: 250 });
  const region = regionBox.debounced;
  const setRegion = regionBox.setValue;
  // Mirror the region box's debounced value into the URL (resets to page 1 like
  // any other filter change). Guarded so hydrating on mount never writes.
  useEffect(() => {
    if (regionBox.debounced !== filters.region) handleFilterChange('region', regionBox.debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionBox.debounced]);
  // The region column is mostly localities ("trasaco", "Yilo Krobo"), so the
  // box stays free text and suggests what the index actually holds.
  const regionSuggestions = useSalesRegionsQuery(region).data ?? [];
  const vertical = filters.vertical;
  const tier = filters.tier;
  const recency = (filters.recency === 'all' ? 'all' : (Number(filters.recency) as 7 | 30 | 90)) as
    | 7
    | 30
    | 90
    | 'all';
  const notInPool = filters.notInPool === '1';
  const hasPhone = filters.hasPhone === '1';
  const presence = filters.presence;
  const sellerKind = filters.sellerKind;
  const inventoryBand = filters.inventoryBand;
  const minListings = filters.minListings;
  const minPosted30 = filters.minPosted30;
  const sort = filters.sort;
  const setVertical = (v: string) => handleFilterChange('vertical', v);
  const setTier = (v: string) => handleFilterChange('tier', v);
  const setRecency = (v: 7 | 30 | 90 | 'all') => handleFilterChange('recency', String(v));
  const setNotInPool = (on: boolean) => handleFilterChange('notInPool', on ? '1' : '');
  const setHasPhone = (on: boolean) => handleFilterChange('hasPhone', on ? '1' : '');
  const setPresence = (v: string) => handleFilterChange('presence', v);
  const setSellerKind = (v: string) => handleFilterChange('sellerKind', v);
  const setInventoryBand = (v: string) => handleFilterChange('inventoryBand', v);
  const setMinListings = (v: string) => handleFilterChange('minListings', v);
  const setMinPosted30 = (v: string) => handleFilterChange('minPosted30', v);
  const setSort = (v: SalesSearchSort) => handleFilterChange('sort', v);
  const setPage = handlePageChange;
  const setLimit = handleLimitChange;
  const [preview, setPreview] = useState<SalesSearchHit | null>(null);
  const [pooledIds, setPooledIds] = useState<Record<string, string>>({});
  const [dupe, setDupe] = useState<{ row: SalesSearchHit; matches: SalesNearMatch[] } | null>(null);

  const search = useSalesSearchQuery({
    q: q.trim() || undefined,
    vertical,
    region: region.trim() || undefined,
    tier,
    hasPhone,
    notInPool,
    recencyDays: recency,
    presence,
    sellerKind,
    minInventory: num(inventoryBand.split('-')[0] ?? ''),
    maxInventory: num(inventoryBand.split('-')[1] ?? ''),
    minListings: num(minListings),
    minPosted30: num(minPosted30),
    sort,
    limit,
    offset: (page - 1) * limit,
  });
  const poolHit = usePoolSearchHitMutation();
  const skipHit = useSkipSearchHitMutation();

  const items = search.data?.items ?? [];
  const total = search.data?.total ?? 0;
  const now = Date.now();
  const forbidden = getApiErrorCode(search.error) === 'upstream_forbidden';

  const filtersOn =
    Boolean(q.trim()) ||
    vertical !== 'all' ||
    Boolean(region.trim()) ||
    tier !== 'paying' ||
    recency !== 'all' ||
    notInPool ||
    hasPhone ||
    presence !== 'all' ||
    sellerKind !== 'all' ||
    Boolean(inventoryBand || minListings || minPosted30);

  const resetFilters = () => {
    setQ('');
    setVertical('all');
    setRegion('');
    setTier('paying');
    setRecency('all');
    setNotInPool(false);
    setHasPhone(false);
    setPresence('all');
    setSellerKind('all');
    setInventoryBand('');
    setMinListings('');
    setMinPosted30('');
  };

  const addHit = (row: SalesSearchHit, confirmNew = false) => {
    if (!rep) {
      promptActAs('add someone to the pool');
      return;
    }
    poolHit.mutate({ hitId: row.hitId, confirmNew }, {
      onSuccess: (subject) => {
        setDupe(null);
        setPooledIds((cur) => ({ ...cur, [row.hitId]: subject.id }));
        toast.success(
          row.onFeelyFeely
            ? `Added ${subject.displayName} to the pool as an existing user — convert, don't onboard`
            : `Added ${subject.displayName} to the pool`,
        );
        setPreview(null);
        navigate(`/admin/sales/prospects/${subject.id}`);
      },
      onError: (err) => {
        const code = getApiErrorCode(err);
        if (code === 'possible_duplicate') {
          setDupe({ row, matches: getApiErrorField<SalesNearMatch[]>(err, 'matches') ?? [] });
          return;
        }
        if (code === 'identity_collision') {
          const other = getApiErrorField<string>(err, 'otherSubjectId');
          toast.message('Already in the pool under another identity.');
          if (other) navigate(`/admin/sales/prospects/${other}`);
          return;
        }
        if (code === 'already_pooled') {
          toast.message('Already in the pool.');
          return;
        }
        toastSales(err);
      },
    });
  };

  const skipRow = (row: SalesSearchHit) => {
    if (!rep) {
      promptActAs('skip a listing');
      return;
    }
    skipHit.mutate(row.hitId, {
      onSuccess: (res) => {
        toast.success(`Skipped — hidden until ${res.skippedUntil.slice(0, 10)}`);
        setPreview((cur) => (cur?.hitId === row.hitId ? null : cur));
      },
      onError: toastSales,
    });
  };

  return (
    <SalesPageShell
      title="Search"
      description="People already listing elsewhere — often paying for reach. Pull the eligible ones into the pool. Adding does not claim them."
      crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }, { label: 'Search' }]}
      icon={Search}
      actions={<CopyLinkButton label="Copy search link" />}
      stats={[
        { key: 'matches', label: 'Matches', value: forbidden ? '—' : total.toLocaleString() },
        { key: 'page', label: 'This page', value: forbidden ? '—' : items.length },
        { key: 'asof', label: 'Index as of', value: search.data?.asOf ? search.data.asOf.slice(0, 10) : '—' },
      ]}
    >
      <p className="mb-5 max-w-3xl text-sm text-gray-600">
        We confirm they are listing on other sites and how active they are. We do not show which sites or their live ads.
      </p>

      <div className="mb-5 space-y-4" role="search" aria-label="Marketplace search">
        <div>
          <label htmlFor="marketplace-search" className="mb-1.5 block text-sm font-medium text-gray-900">
            Search
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" aria-hidden />
            <input
              id="marketplace-search"
              type="search"
              autoComplete="off"
              className="h-11 w-full rounded-lg border border-gray-400 bg-white py-2 pl-10 pr-11 text-sm text-gray-900 placeholder:text-gray-500 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
              value={qValue}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or phone (any format: 024…, +233 24…)"
            />
            {qValue ? (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium text-gray-900">Filters</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div role="group" aria-label="Rank by">
                <Segmented
                  value={sort}
                  onChange={setSort}
                  options={[
                    { id: 'value', label: 'Inventory' },
                    { id: 'count', label: 'Listings' },
                    { id: 'recent', label: 'Recent' },
                    { id: 'velocity', label: 'Posting' },
                  ]}
                />
              </div>
              {filtersOn ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm font-medium text-gray-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            <FilterField id="mkt-tier" label="Promo">
              <select
                id="mkt-tier"
                className={FILTER_SELECT}
                value={tier}
                onChange={(e) => setTier(e.target.value as SalesSearchTier | 'all')}
              >
                <option value="paying">Paying for promo</option>
                <option value="premium">Featured plan</option>
                <option value="free">No paid promo</option>
                <option value="all">Anyone listing</option>
              </select>
            </FilterField>
            <FilterField id="mkt-category" label="Desk">
              <select
                id="mkt-category"
                className={FILTER_SELECT}
                value={vertical}
                onChange={(e) => setVertical(e.target.value as SalesVertical | 'all')}
              >
                <option value="all">All</option>
                {SALES_VERTICALS.map((v) => (
                  <option key={v} value={v}>
                    {VERTICAL_LABEL[v]}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="mkt-region" label="Region">
              <input
                id="mkt-region"
                list="mkt-region-options"
                autoComplete="off"
                className={FILTER_SELECT}
                value={regionBox.value}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="Any region or area"
              />
              <datalist id="mkt-region-options">
                {regionSuggestions.map((r) => (
                  <option key={r.region} value={r.region}>
                    {r.sellers.toLocaleString()} sellers
                  </option>
                ))}
              </datalist>
            </FilterField>
            <FilterField id="mkt-seen" label="Last active">
              <select
                id="mkt-seen"
                className={FILTER_SELECT}
                value={String(recency)}
                onChange={(e) => setRecency(e.target.value === 'all' ? 'all' : (Number(e.target.value) as 7 | 30 | 90))}
              >
                <option value="all">Any time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </FilterField>
            <div className="flex items-end">
              <label className="flex h-10 items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-400 text-slate-900 focus:ring-slate-900"
                  checked={notInPool}
                  onChange={(e) => setNotInPool(e.target.checked)}
                />
                Not in pool yet
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex h-10 items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-400 text-slate-900 focus:ring-slate-900"
                  checked={hasPhone}
                  onChange={(e) => setHasPhone(e.target.checked)}
                />
                Has a phone
              </label>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            <FilterField id="mkt-presence" label="On FeelyFeely">
              <select
                id="mkt-presence"
                className={FILTER_SELECT}
                value={presence}
                onChange={(e) => setPresence(e.target.value as SalesSearchPresence | 'all')}
              >
                <option value="all">Anyone</option>
                <option value="to_onboard">Not on FF — onboard</option>
                <option value="on_ff">Already on FF — convert</option>
              </select>
            </FilterField>
            <FilterField id="mkt-kind" label="Seller type">
              <select
                id="mkt-kind"
                className={FILTER_SELECT}
                value={sellerKind}
                onChange={(e) => setSellerKind(e.target.value as SalesSellerKind | 'all')}
              >
                <option value="all">Any</option>
                <option value="business">Business / agency</option>
                <option value="individual">Individual</option>
              </select>
            </FilterField>
            <FilterField id="mkt-inventory" label="Inventory">
              <select
                id="mkt-inventory"
                className={FILTER_SELECT}
                value={inventoryBand}
                onChange={(e) => setInventoryBand(e.target.value)}
              >
                {INVENTORY_BANDS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="mkt-min-live" label="Live listings">
              <select
                id="mkt-min-live"
                className={FILTER_SELECT}
                value={minListings}
                onChange={(e) => setMinListings(e.target.value)}
              >
                <option value="">Any</option>
                {LIVE_LISTINGS.map((n) => (
                  <option key={n} value={n}>
                    {n}+
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="mkt-min-posted" label="Posted last 30 days">
              <select
                id="mkt-min-posted"
                className={FILTER_SELECT}
                value={minPosted30}
                onChange={(e) => setMinPosted30(e.target.value)}
              >
                <option value="">Any</option>
                {POSTED_30D.map((n) => (
                  <option key={n} value={n}>
                    {n}+
                  </option>
                ))}
              </select>
            </FilterField>
          </div>
        </div>
      </div>

      {search.isError ? (
        <MarketplaceIndexState error={search.error} />
      ) : search.isLoading ? (
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sellers…
        </div>
      ) : search.data?.note && items.length === 0 ? (
        <EmptyState label={search.data.note} />
      ) : items.length === 0 ? (
        <EmptyState label="No matches for these filters." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <caption className="sr-only">People listing elsewhere, ranked for outreach</caption>
            <thead className="border-b border-gray-200 text-left text-xs font-medium text-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Person
                </th>
                <th scope="col" className="px-4 py-3">
                  Desk
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Inventory
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Live
                </th>
                <th scope="col" className="px-4 py-3">
                  Last active
                </th>
                <th scope="col" className="px-4 py-3">
                  Pool
                </th>
                <th scope="col" className="px-4 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const pooled = row.inPool || Boolean(pooledIds[row.hitId]);
                const subjectId = pooledIds[row.hitId] || row.subjectId;
                return (
                  <tr key={row.hitId} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-left font-medium text-gray-900 hover:text-slate-700"
                        onClick={() => setPreview(row)}
                      >
                        {row.displayName}
                      </button>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {row.premium ? (
                          <span className="rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-800">
                            Featured
                          </span>
                        ) : row.paid ? (
                          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                            Paying
                          </span>
                        ) : null}
                        {row.onFeelyFeely ? (
                          <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">
                            On FeelyFeely
                          </span>
                        ) : null}
                        {row.sellerKind === 'business' ? (
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                            Business
                          </span>
                        ) : null}
                      </div>
                      {row.phone ? <p className="mt-0.5 text-xs text-gray-500"><PhoneValue phone={row.phone} /></p> : null}
                      <p className="mt-0.5 text-xs text-gray-500">{row.region || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-800">
                      {row.verticals.map((v) => VERTICAL_LABEL[v] ?? v).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                      {row.inventoryValueGhs ? compactGhs(row.inventoryValueGhs) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900">{row.listingCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-800">{seenLabel(row.lastSeen, now)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        label={pooled ? 'In pool' : 'Not in pool'}
                        className={
                          pooled
                            ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      {pooled && subjectId ? (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={btnPrimary}
                            onClick={() => navigate(`/admin/sales/communications?subject=${subjectId}`)}
                          >
                            Message
                          </button>
                          <button
                            type="button"
                            className={btnGhost}
                            onClick={() => navigate(`/admin/sales/prospects/${subjectId}`)}
                          >
                            Open in pool
                          </button>
                        </div>
                      ) : pooled ? (
                        <span className="text-xs text-gray-500">Already pooled</span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={btnPrimary}
                            disabled={(Boolean(rep) && !canWrite) || poolHit.isPending}
                            onClick={() => addHit(row)}
                            title={row.onFeelyFeely ? 'Already a FeelyFeely user — pooled for conversion' : undefined}
                          >
                            {row.onFeelyFeely ? 'Add to convert' : 'Add to pool'}
                          </button>
                          <button
                            type="button"
                            className={btnGhost}
                            disabled={(Boolean(rep) && !canWrite) || skipHit.isPending}
                            onClick={() => skipRow(row)}
                          >
                            Skip
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="border-t border-gray-200 px-4 py-3">
            <Pagination
              total={total}
              page={page}
              limit={limit}
              itemsPerPage={limit}
              setPage={setPage}
              setItemsPerPage={(n) => {
                setLimit(n);
                setPage(1);
              }}
              showSelect
              selectOptions={[10, 20, 50]}
              compact
            />
          </div>
        </div>
      )}

      {preview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mkt-preview-title"
        >
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 id="mkt-preview-title" className="text-base font-semibold text-gray-900">
                  {preview.displayName}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Listing elsewhere · {preview.region || 'region unknown'}
                  {preview.listedSince ? ` · since ${preview.listedSince.slice(0, 10)}` : ''}
                </p>
              </div>
              <StatusBadge
                label={preview.inPool || pooledIds[preview.hitId] ? 'In pool' : 'Not in pool'}
                className={
                  preview.inPool || pooledIds[preview.hitId]
                    ? 'bg-sky-50 text-sky-700 border-sky-200'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {preview.premium ? (
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                  Featured
                </span>
              ) : preview.paid ? (
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                  Paying
                </span>
              ) : (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">No paid promo</span>
              )}
              {preview.onFeelyFeely ? (
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                  On FeelyFeely
                </span>
              ) : null}
            </div>
            {preview.onFeelyFeely ? (
              <p className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">
                This phone already has a FeelyFeely account. Pool them to convert — get their listings and promo spend
                here — not to sign them up.
              </p>
            ) : null}
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm text-gray-700">
              <div>
                <dt className="text-xs text-gray-500">Inventory</dt>
                <dd className="tabular-nums font-medium">
                  {preview.inventoryValueGhs ? compactGhs(preview.inventoryValueGhs) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Live listings</dt>
                <dd className="tabular-nums font-medium">{preview.listingCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Posted last 30d</dt>
                <dd className="tabular-nums font-medium">{preview.listingsLast30}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Last active</dt>
                <dd>{seenLabel(preview.lastSeen, now)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Desk</dt>
                <dd>{preview.verticals.map((v) => VERTICAL_LABEL[v] ?? v).join(', ') || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Phone</dt>
                <dd><PhoneValue phone={preview.phone} /></dd>
              </div>
            </dl>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setPreview(null)}>
                Close
              </button>
              {preview.inPool || pooledIds[preview.hitId] ? (
                (pooledIds[preview.hitId] || preview.subjectId) ? (
                  <>
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() => navigate(`/admin/sales/prospects/${pooledIds[preview.hitId] || preview.subjectId}`)}
                    >
                      Open in pool
                    </button>
                    <button
                      type="button"
                      className={btnPrimary}
                      onClick={() => navigate(`/admin/sales/communications?subject=${pooledIds[preview.hitId] || preview.subjectId}`)}
                    >
                      Message
                    </button>
                  </>
                ) : null
              ) : (
                <>
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={(Boolean(rep) && !canWrite) || skipHit.isPending}
                    onClick={() => skipRow(preview)}
                  >
                    Skip
                  </button>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={(Boolean(rep) && !canWrite) || poolHit.isPending}
                    onClick={() => addHit(preview)}
                  >
                    {preview.onFeelyFeely ? 'Add to convert' : 'Add to pool'}
                  </button>
                </>
              )}
            </div>
            <p className="mt-3 text-center text-xs text-gray-400">
              <Link to="/admin/sales/prospects" className="hover:underline">
                Open pool
              </Link>
            </p>
          </div>
        </div>
      ) : null}

      {dupe ? (
        <DuplicateMatchesDialog
          name={dupe.row.displayName}
          matches={dupe.matches}
          busy={poolHit.isPending}
          onOpen={(id) => navigate(`/admin/sales/prospects/${id}`)}
          onConfirm={() => addHit(dupe.row, true)}
          onCancel={() => setDupe(null)}
        />
      ) : null}
    </SalesPageShell>
  );
}
